import React, { useEffect } from 'react';
import { Prompt } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { notification } from '../../../../general/kitMethods';
import { getCompletedInBackground, getUploadStats } from '../selectors';

const LEAVE_MESSAGE =
  'Files are still uploading. If you leave now the upload will be interrupted. Leave anyway?';

const plural = (n: number) => (n === 1 ? 'file' : 'files');

/**
 * Mounted once inside the router. Guards against losing in-flight uploads when
 * the user closes the tab / reloads (beforeunload) or navigates away (Prompt),
 * and shows a toast once an upload batch finishes while the modal is closed.
 */
export const UploadGuard: React.FC = () => {
  const { isUploading, successfulCount, failedCount } = useSelector(getUploadStats);
  const completedInBackground = useSelector(getCompletedInBackground);

  // Native confirmation on tab/browser close or reload while uploading.
  useEffect(() => {
    if (!isUploading) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isUploading]);

  // Toast when a batch finishes while the user is elsewhere (modal closed).
  useEffect(() => {
    if (!completedInBackground) return;
    if (failedCount > 0) {
      notification.warning({
        message: 'Upload finished',
        description: `${successfulCount} ${plural(successfulCount)} uploaded, ${failedCount} failed.`,
      });
    } else {
      notification.success({
        message: 'Upload complete',
        description: `${successfulCount} ${plural(successfulCount)} uploaded to Asset Lab.`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedInBackground]);

  return <Prompt when={isUploading} message={LEAVE_MESSAGE} />;
};
